import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import AuthRoutes from './AuthRoutes';
import { NotFound } from '../components';
import { useAuth } from '../context/AuthContext';
import { useUserRolesAndPermissions } from '../context/UserRolesAndPermissionsContext';
import { PAGE_KEYS, USER_ROLE_KEYS } from '../utilities/constants';
import PropTypes from 'prop-types';
import DeviceManagementMobileHome from '../pages/DeviceManagement/components/DeviceManagementMobileHome';

// Lazy load all pages
const VehicleMonitoring = lazy(
  () => import('../pages/VehicleMonitoring/VehicleMonitoring')
);
const Overview = lazy(() => import('../pages/Overview/Overview'));
const Broadcast = lazy(() => import('../pages/Broadcast/Broadcast'));
const SafetyAndAnomalyMonitoring = lazy(
  () => import('../pages/SafetyAndAnomalyMonitoring/SafetyAndAnomalyMonitoring')
);
const HistoryAndReports = lazy(
  () => import('../pages/HistoryAndReports/HistoryAndReports')
);
const UserManagement = lazy(
  () => import('../pages/UserManagement/UserManagement')
);
const DeviceManagement = lazy(
  () => import('../pages/DeviceManagement/DeviceManagement')
);
const SuperAdminConsole = lazy(
  () => import('../pages/SuperAdminConsole/SuperAdminConsole')
);
const DriverMonitoringSystem = lazy(
  () => import('../pages/DriverMonitoringSystem/DriverMonitoringSystem')
);
const ChangePasswordForm = lazy(
  () => import('../pages/Authorization/ChangePasswordForm/ChangePasswordForm')
);

const TripSurveillanceAndAnalysis = lazy(
  () =>
    import('../pages/TripSurveillanceAndAnalysis/TripSurveillanceAndAnalysis')
);

const TripSchedulingAndManagement = lazy(
  () =>
    import('../pages/TripSchedulingAndManagement/TripSchedulingAndManagement')
);

// History and Reports sub-pages
const AnomalyReport = lazy(
  () => import('../pages/HistoryAndReports/components/AnomalyReport')
);
const SosReport = lazy(
  () => import('../pages/HistoryAndReports/components/SosReport')
);
const TripsReport = lazy(
  () => import('../pages/HistoryAndReports/components/TripsReport')
);
const DriverDrowsinessReport = lazy(
  () => import('../pages/HistoryAndReports/components/DriverDrowsinessReport')
);

const CameraErrorReports = lazy(
  () => import('../pages/HistoryAndReports/components/CameraErrorReports')
);

// User Management sub-pages
const UserManagementTabs = lazy(
  () => import('../pages/UserManagement/components/UserManagementTabs')
);
const RegisterLocation = lazy(
  () => import('../pages/UserManagement/components/RegisterLocation')
);

// Executive Console sub-pages
const RegisterCompany = lazy(
  () => import('../pages/ExecutiveConsole/components/RegisterCompany')
);

// Device Management sub-pages
const DeviceDeregistration = lazy(
  () =>
    import(
      '../pages/DeviceManagement/components/DeviceDeregistration/DeviceDeregistration'
    )
);
const DeviceInstallation = lazy(
  () =>
    import(
      '../pages/DeviceManagement/components/DeviceInstallation/DeviceInstallation'
    )
);
const DeviceManagementHome = lazy(
  () => import('../pages/DeviceManagement/components/DeviceManagementHome')
);
const DeviceOnboarding = lazy(
  () =>
    import(
      '../pages/DeviceManagement/components/DeviceOnboarding/DeviceOnboarding'
    )
);
const DeviceReplacement = lazy(
  () =>
    import(
      '../pages/DeviceManagement/components/DeviceReplacement/DeviceReplacement'
    )
);
const DeviceUninstallation = lazy(
  () =>
    import(
      '../pages/DeviceManagement/components/DeviceUninstallation/DeviceUninstallation'
    )
);

// Super Admin Console sub-pages
const SuperAdminConsoleHome = lazy(
  () => import('../pages/SuperAdminConsole/components/SuperAdminConsoleHome')
);

const PrivateRoute = ({ children }) => {
  const { isLoggedIn } = useAuth();
  return isLoggedIn ? (
    <Suspense fallback={null}>{children}</Suspense>
  ) : (
    <Navigate to="/login" />
  );
};

PrivateRoute.propTypes = {
  children: PropTypes.node,
};

const getAdminConsoleRoutes = basePath => (
  <Route
    path={basePath}
    element={
      <PrivateRoute>
        <SuperAdminConsole />
      </PrivateRoute>
    }
  >
    <Route
      path=""
      element={
        <PrivateRoute>
          <SuperAdminConsoleHome />
        </PrivateRoute>
      }
    />
    <Route
      path="register-a-new-company"
      element={
        <PrivateRoute>
          <RegisterCompany />
        </PrivateRoute>
      }
    />
    <Route
      path="edit-company-information"
      element={
        <PrivateRoute>
          <RegisterCompany type="edit" />
        </PrivateRoute>
      }
    />
    <Route
      path="install-a-new-device"
      element={
        <PrivateRoute>
          <DeviceInstallation />
        </PrivateRoute>
      }
    />
    <Route
      path="onboarding-checklist"
      element={
        <PrivateRoute>
          <DeviceOnboarding />
        </PrivateRoute>
      }
    />
    <Route
      path="de-registration-of-customer"
      element={
        <PrivateRoute>
          <DeviceDeregistration />
        </PrivateRoute>
      }
    />
    <Route
      path="replace-old-device"
      element={
        <PrivateRoute>
          <DeviceReplacement />
        </PrivateRoute>
      }
    />
    <Route
      path="uninstall-device"
      element={
        <PrivateRoute>
          <DeviceUninstallation />
        </PrivateRoute>
      }
    />
  </Route>
);

const AppRoutes = () => {
  const { isPageAccessible, userRole, selectedOrg, isViewOnly } =
    useUserRolesAndPermissions();

  return (
    <Routes>
      {AuthRoutes()}
      <Route
        path="*"
        element={
          <PrivateRoute>
            <NotFound />
          </PrivateRoute>
        }
      />
      <Route
        path="/change-password"
        element={
          <PrivateRoute>
            <ChangePasswordForm />
          </PrivateRoute>
        }
      />
      {isPageAccessible(PAGE_KEYS.VEHICLE_MONITORING) && (
        <Route
          path="/vehicle-monitoring"
          element={
            <PrivateRoute>
              <VehicleMonitoring key={selectedOrg} />
            </PrivateRoute>
          }
        />
      )}
      {isPageAccessible(PAGE_KEYS.DRIVER_MONITORING_SYSTEM) && (
        <Route
          path="/driver-monitoring-system"
          element={
            <PrivateRoute>
              <DriverMonitoringSystem key={selectedOrg} />
            </PrivateRoute>
          }
        />
      )}
      {isPageAccessible(PAGE_KEYS.OVERVIEW) && (
        <Route
          path="/overview"
          element={
            <PrivateRoute>
              <Overview key={selectedOrg} />
            </PrivateRoute>
          }
        />
      )}
      {isPageAccessible(PAGE_KEYS.BROADCAST) && (
        <Route
          path="/broadcast"
          element={
            <PrivateRoute>
              <Broadcast key={selectedOrg} />
            </PrivateRoute>
          }
        />
      )}
      {isPageAccessible(PAGE_KEYS.SAFETY_AND_ANOMALY_MONITORING) && (
        <Route
          path="/safety-and-anomaly-monitoring"
          element={
            <PrivateRoute>
              <SafetyAndAnomalyMonitoring key={selectedOrg} />
            </PrivateRoute>
          }
        />
      )}
      {isPageAccessible(PAGE_KEYS.DEVICE_MANAGEMENT) && (
        <Route
          path="/device-management"
          element={
            <PrivateRoute>
              <DeviceManagement />
            </PrivateRoute>
          }
        >
          <Route
            path=""
            element={
              <PrivateRoute>
                <DeviceManagementHome />
              </PrivateRoute>
            }
          />
        </Route>
      )}
      {isPageAccessible(PAGE_KEYS.DEVICE_MANAGEMENT) &&
        !isViewOnly(PAGE_KEYS.DEVICE_MANAGEMENT) && (
          <Route
            path="/mobile/device-management"
            element={
              <PrivateRoute>
                <DeviceManagement />
              </PrivateRoute>
            }
          >
            <Route
              path=""
              element={
                <PrivateRoute>
                  <DeviceManagementMobileHome />
                </PrivateRoute>
              }
            />
            <Route
              path="install-a-new-device"
              element={
                <PrivateRoute>
                  <DeviceInstallation />
                </PrivateRoute>
              }
            />
            <Route
              path="replace-old-device"
              element={
                <PrivateRoute>
                  <DeviceReplacement />
                </PrivateRoute>
              }
            />
            <Route
              path="uninstall-device"
              element={
                <PrivateRoute>
                  <DeviceUninstallation />
                </PrivateRoute>
              }
            />
          </Route>
        )}
      {isPageAccessible(PAGE_KEYS.HISTORY_AND_REPORTS) && (
        <Route
          path="/history-and-reports"
          element={
            <PrivateRoute>
              <HistoryAndReports key={selectedOrg} />
            </PrivateRoute>
          }
        >
          <Route
            path="trips-report"
            element={
              <PrivateRoute>
                <TripsReport />
              </PrivateRoute>
            }
          />
          <Route
            path="sos-report"
            element={
              <PrivateRoute>
                <SosReport />
              </PrivateRoute>
            }
          />
          <Route
            path="anomaly-report"
            element={
              <PrivateRoute>
                <AnomalyReport />
              </PrivateRoute>
            }
          />
          <Route
            path="driver-drowsiness-report"
            element={
              <PrivateRoute>
                <DriverDrowsinessReport />
              </PrivateRoute>
            }
          />
          <Route
            path="camera-error-report"
            element={
              <PrivateRoute>
                <CameraErrorReports />
              </PrivateRoute>
            }
          />
        </Route>
      )}

      {isPageAccessible(PAGE_KEYS.USER_MANAGEMENT) && (
        <Route
          path="/user-management"
          element={
            <PrivateRoute>
              <UserManagement key={selectedOrg} />
            </PrivateRoute>
          }
        >
          <Route
            path=""
            element={
              <PrivateRoute>
                <UserManagementTabs />
              </PrivateRoute>
            }
          />
          <Route
            path="register-a-new-location"
            element={
              <PrivateRoute>
                <RegisterLocation />
              </PrivateRoute>
            }
          />
        </Route>
      )}
      {userRole === USER_ROLE_KEYS.SUPER_ADMIN &&
        getAdminConsoleRoutes('/super-admin-console')}
      {userRole === USER_ROLE_KEYS.PLATFORM_OPERATION_MANAGER &&
        getAdminConsoleRoutes('/platform-operations-console')}
      {isPageAccessible(PAGE_KEYS.TRIP_SURVEILLANCE_AND_ANALYSIS) && (
        <Route
          path="/trip-surveillance-and-analysis"
          element={
            <PrivateRoute>
              <TripSurveillanceAndAnalysis />
            </PrivateRoute>
          }
        />
      )}
      {isPageAccessible(PAGE_KEYS.TRIP_SCHEDULING_AND_MANAGEMENT) && (
        <Route
          path="/trip-scheduling-and-management"
          element={
            <PrivateRoute>
              <TripSchedulingAndManagement />
            </PrivateRoute>
          }
        />
      )}
    </Routes>
  );
};

export default AppRoutes;
